import {
  UpdateReportSettingsRequest,
  GetReportSettingsRequest,
  GetSectionReportRequest,
  GetUserReportVersionRequest,
} from '../api/requests';
import type { UserStore } from '~/authentication/stores/user';
import type ApiClient from '~/common/services/ApiClient';
import { Filter, Setting, InputSetting } from '~/reports/types';

export class ReportService {
  apiClient: ApiClient;
  userStore: UserStore;

  constructor(apiClient: ApiClient, userStore: UserStore) {
    this.apiClient = apiClient;
    this.userStore = userStore;
  }

  async updateSettings(settings: Setting, params?: { include: string }) {
    const accessToken = this.userStore.accessToken;

    const transformedSettings = this.transformSetting(settings);

    const request = new UpdateReportSettingsRequest({
      token: accessToken,
      settings: transformedSettings,
      params: { ...params },
    });
    return this.apiClient.request(request);
  }

  async getReportSettings(params?: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new GetReportSettingsRequest({
      token: accessToken,
      params,
    });
    return this.apiClient.request(request);
  }

  async getSectionReport(params?: {
    filter: Filter;
    include: string;
    sectionTitles?: string[];
  }) {
    const accessToken = this.userStore.accessToken;
    const request = new GetSectionReportRequest({
      token: accessToken,
      params,
    });
    return this.apiClient.request(request);
  }

  transformSetting = (rawSetting: Setting) => {
    const setting = JSON.parse(JSON.stringify(rawSetting));

    const sanitizeInput = (input: any): InputSetting => {
      const {
        checked,
        id,
        input: inputValue,
        compare,
        value,
        remoteDataOption,
        conditionalFields = [],
        subInputs = [],
        options,
      } = input;

      const sanitizedConditionalFields = conditionalFields.map((field: any) => {
        if ('subInputs' in field && 'title' in field) {
          return {
            id: field.id,
            title: field.title,
            checked: field.checked,
            subInputs: field.subInputs.map(sanitizeInput),
          };
        } else {
          return sanitizeInput(field);
        }
      });

      const sanitizedSubInputs = subInputs.map(sanitizeInput);

      const sanitized: InputSetting = {
        checked,
        id,
        input: inputValue,
        compare,
        value,
        remoteDataOption,
        conditionalFields: sanitizedConditionalFields,
        subInputs: sanitizedSubInputs,
        options,
      };

      return sanitized;
    };

    setting.sections.forEach((section: any) => {
      delete section.removedFromTemplate;

      section.subInputs = section.subInputs.map(sanitizeInput);
    });

    return setting;
  };

  async getUserReport(userId: string, params?: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new GetUserReportVersionRequest({
      token: accessToken,
      params,
      userId,
    });

    return this.apiClient.request(request);
  }
}
